import { add, append, set, update } from '@/vuex/helpers';
import { replaceDiacriticsInString } from '@/helpers/replaceDiacriticsInString';
import baseURLv1 from '@/services/APIConfig';

const createSortFunction = (isDescending, sortField) => (a, b) => {
  if (a[sortField] > b[sortField]) return isDescending ? -1 : 1;
  if (a[sortField] < b[sortField]) return isDescending ? 1 : -1;
  return 0;
};

const createFullnameSortFunction = (isDescending) => (a, b) => {
  const nameA = replaceDiacriticsInString(a.fullname);
  const nameB = replaceDiacriticsInString(b.fullname);

  if (nameA > nameB) return isDescending ? -1 : 1;
  if (nameA < nameB) return isDescending ? 1 : -1;
  return 0;
};

export default {
  namespaced: true,
  state: {
    isMessagingMetricsByUserLoading: true,
    messagingMetricsByUser: [],
    averageMessageResponseTime: null,
    messagingMetricsFilters: {
      order_by: '',
      date_from: '',
      date_to: '',
      company_id: '',
    },
    isPermitStatusMetricsByUserLoading: true,
    permitStatusMetricsByUser: [],
    permitStatusTotalMetrics: {},
    permitStatusMetricsFilters: {
      order_by: '',
      date_from: '',
      date_to: '',
      company_id: '',
    },
    isPermitActionDatesMetricsByUserLoading: true,
    permitActionDatesMetricsByUser: [],
    permitActionDatesTotalMetrics: {},
    permitActionDatesMetricsFilters: {
      order_by: '',
      date_from: '',
      date_to: '',
      company_id: '',
    },
  },
  mutations: {
    set,
    update,
    add,
    append,

    sortMessagingMetricsByUser(state, { field, payload }) {
      const isDescending = payload.startsWith('-');
      const sortField = isDescending ? payload.slice(1) : payload;

      state.messagingMetricsFilters[field] = payload;

      state.messagingMetricsByUser.sort(
        payload.includes('fullname')
          ? createFullnameSortFunction(isDescending)
          : createSortFunction(isDescending, sortField),
      );
    },

    sortPermitStatusMetricsByUser(state, { field, payload }) {
      const isDescending = payload.startsWith('-');
      const sortField = isDescending ? payload.slice(1) : payload;

      state.permitStatusMetricsFilters[field] = payload;

      state.permitStatusMetricsByUser.sort(
        payload.includes('fullname')
          ? createFullnameSortFunction(isDescending)
          : createSortFunction(isDescending, sortField),
      );
    },

    sortPermitActionDatesMetricsByUser(state, { field, payload }) {
      const isDescending = payload.startsWith('-');
      const sortField = isDescending ? payload.slice(1) : payload;

      state.permitActionDatesMetricsFilters[field] = payload;

      state.permitActionDatesMetricsByUser.sort(
        payload.includes('fullname')
          ? createFullnameSortFunction(isDescending)
          : createSortFunction(isDescending, sortField),
      );
    },
    updateFilter(state, { metricType, field, payload }) {
      state[metricType][field] = payload;
    },
  },
  actions: {
    async loadMessagingUsersMetrics({ rootState, state, commit }) {
      const queryParams = new URLSearchParams();
      Object.entries(state.messagingMetricsFilters).forEach(([key, value]) => {
        if (key !== 'order_by' && value ) {
          queryParams.append(key, value);
        }
      });
      const response = await this.$axios.get(`${baseURLv1}/dashboard/metrics/message-response-time/?${queryParams.toString()}`);
      const users = rootState.v2.admin.cms.internal.users.allUsers;

      const messagingMetricsByUser = response.data.data.map((userMetric) => {
        const user = users.find((u) => u.id === userMetric.actor_id);
        if (user) {
          return {
            ...userMetric,
            fullname: user.fullname,
          };
        }
        return {
          ...userMetric,
          fullname: 'Unknown User',
        };
      });

      commit('set', ['messagingMetricsByUser', messagingMetricsByUser]);
      commit('set', ['isMessagingMetricsByUserLoading', false]);
    },
    async loadMessagingAverageMetrics({ state, commit }) {
      const queryParams = new URLSearchParams();
      Object.entries(state.messagingMetricsFilters).forEach(([key, value]) => {
        if (key !== 'order_by' && value ) {
          queryParams.append(key, value);
        }
      });
      const response = await this.$axios.get(`${baseURLv1}/dashboard/metrics/message-response-time/overall-avg?${queryParams.toString()}`);
      const averageResponseTime = response.data.data.avg_resp;

      commit('set', ['averageMessageResponseTime', averageResponseTime]);
    },
    async loadPermitStatusMetrics({ rootState, state, commit }) {
      const queryParams = new URLSearchParams();
      Object.entries(state.permitStatusMetricsFilters).forEach(([key, value]) => {
        if (key !== 'order_by' && value ) {
          queryParams.append(key, value);
        }
      });
      const response = await this.$axios.get(`${baseURLv1}/dashboard/metrics/permit-status-fulfillment/?${queryParams.toString()}`);
      const users = rootState.v2.admin.cms.internal.users.allUsers;

      const permitStatusMetricsByUser = response.data.data.map((userMetric) => {
        const user = users.find((u) => u.id === userMetric.actor_id);
        if (user) {
          return {
            ...userMetric,
            fullname: user.fullname,
          };
        }
        return {
          ...userMetric,
          fullname: 'Unknown User',
        };
      });

      commit('set', ['permitStatusMetricsByUser', permitStatusMetricsByUser]);
      commit('set', ['isPermitStatusMetricsByUserLoading', false]);
    },
    async loadPermitStatusAverageMetrics({ state, commit }) {
      const queryParams = new URLSearchParams();
      Object.entries(state.permitStatusMetricsFilters).forEach(([key, value]) => {
        if (key !== 'order_by' && value ) {
          queryParams.append(key, value);
        }
      });
      const response = await this.$axios.get(`${baseURLv1}/dashboard/metrics/permit-status-fulfillment/funnel/?${queryParams.toString()}`);
      const permitStatusTotalMetrics = response.data.data;

      commit('set', ['permitStatusTotalMetrics', permitStatusTotalMetrics]);
    },
    async loadPermitActionDatesMetrics({ rootState, state,  commit }) {
      const queryParams = new URLSearchParams();
      Object.entries(state.permitActionDatesMetricsFilters).forEach(([key, value]) => {
        if (key !== 'order_by' && value ) {
          queryParams.append(key, value);
        }
      });
      const response = await this.$axios.get(`${baseURLv1}/dashboard/metrics/permit-action-date-fulfillment/?${queryParams.toString()}`);
      const users = rootState.v2.admin.cms.internal.users.allUsers;

      const permitActionDatesMetricsByUser = response.data.data.map((userMetric) => {
        const user = users.find((u) => u.id === userMetric.actor_id);
        if (user) {
          return {
            ...userMetric,
            fullname: user.fullname,
          };
        }
        return {
          ...userMetric,
          fullname: 'Unknown User',
        };
      });

      commit('set', ['permitActionDatesMetricsByUser', permitActionDatesMetricsByUser]);
      commit('set', ['isPermitActionDatesMetricsByUserLoading', false]);
    },
    async loadPermitActionDateAverageMetrics({ state, commit }) {
      const queryParams = new URLSearchParams();
      Object.entries(state.permitActionDatesMetricsFilters).forEach(([key, value]) => {
        if (key !== 'order_by' && value ) {
          queryParams.append(key, value);
        }
      });
      const response = await this.$axios.get(`${baseURLv1}/dashboard/metrics/permit-action-date-fulfillment/funnel/?${queryParams.toString()}`);
      console.log('Permit action date average metrics response:', response);
      const permitActionDatesTotalMetrics = response.data.data;

      commit('set', ['permitActionDatesTotalMetrics', permitActionDatesTotalMetrics]);
    },
  },
};
